// MIT License - Copyright (c) 2026 Doug McNall
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import ForgotPassword from './ForgotPassword';
import './AdminLogin.css';

const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [challengeResult, setChallengeResult] = useState(null);
  const [showForgot, setShowForgot] = useState(false);
  const { login, changePassword } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const result = await login(email, password);
    
    if (result.success) {
      // Login successful, user should be redirected by AuthGuard
    } else if (result.requiresPasswordChange) {
      setRequiresPasswordChange(true);
      setChallengeResult(result.challengeResult);
      setError(''); // Clear error since this is expected
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    setLoading(true);
    setError('');

    const result = await changePassword(newPassword, challengeResult);
    
    if (result.success) {
      // Password changed successfully, user should be redirected by AuthGuard
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  if (showForgot) {
    return <ForgotPassword onBack={() => setShowForgot(false)} />;
  }

  if (requiresPasswordChange) {
    return (
      <div className="admin-login">
        <div className="admin-login-container">
          <div className="admin-login-header">
            <h1>D&D Hot Sauce</h1>
            <h2>Change Password</h2>
            <p>You must change your temporary password before continuing.</p>
          </div>
          
          <form onSubmit={handlePasswordChange} className="admin-login-form">
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                placeholder="Enter new password"
                disabled={loading}
                minLength={8}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmNewPassword">Confirm New Password</label>
              <input
                type="password"
                id="confirmNewPassword"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                placeholder="Confirm new password"
                disabled={loading}
                minLength={8}
              />
            </div>
            
            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
            
            <button 
              type="submit" 
              className="login-button"
              disabled={loading}
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
          
          <div className="admin-login-footer">
            <p>Password must be at least 8 characters long</p>
            <p>Include uppercase, lowercase, and numbers for security</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-login">
      <div className="admin-login-container">
        <div className="admin-login-header">
          <h1>D&D Hot Sauce</h1>
          <h2>Admin Portal</h2>
        </div>
        
        <form onSubmit={handleSubmit} className="admin-login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="admin@ddhotsauce.com"
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
              disabled={loading}
            />
          </div>
          
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
          
          <button 
            type="submit" 
            className="login-button"
            disabled={loading}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>

          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => { setError(''); setShowForgot(true); }}
              disabled={loading}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--admin-interactive-primary)',
                cursor: 'pointer',
                fontSize: '0.9rem',
                textDecoration: 'underline',
                padding: 0,
              }}
            >
              Forgot password?
            </button>
          </div>
        </form>
        
        <div className="admin-login-footer">
          <p>Authorized personnel only</p>
          <p>Contact IT support for access issues</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;